import { useMutation, useQuery } from "@tanstack/react-query";
import { useTonAddress, useTonConnectUI } from "@tonconnect/ui-react";
import BigNumber from "bignumber.js";
import { Address, fromNano, toNano } from "ton-core";
import { Deployer } from "../wrappers/Deployer";
import { buildOnchainMetadata } from "../wrappers/onchainContent";
import { getTonClient } from "./tonClient";
import { getSeqno, waitWalletUpdate } from "./walletService";

const DeployerAddress = process.env.REACT_APP_DEPLOYER!;

export interface DeployParams {
  name: string;
  image: string;
  symbol: string;
  description: string;
  amount: string;
  quorum: string;
  decimals: string;
}

export const formatCoinValue = (
  amount: BigNumber.Value,
  decimals: number | string
): string => {
  const decimalsValue = parseInt(String(decimals));
  return new BigNumber(amount)
    .div(Math.pow(10, decimalsValue))
    .toFixed(decimalsValue > 0 ? 2 : 0);
};

export const formatDecimals = (
  amount: BigNumber.Value,
  decimals: number
): string => {
  return new BigNumber(amount).multipliedBy(Math.pow(10, decimals)).toFixed(0);
};

export const useDeployPrice = () => {
  return useQuery<string, Error>([DeployerAddress, "price"], async () => {
    const tonClient = await getTonClient();
    const contract = tonClient.open(
      Deployer.createFromAddress(Address.parse(DeployerAddress))
    );
    const data = await contract.getContractData();
    return fromNano(data.price);
  });
};

export const useDeployMutation = () => {
  const [tonConnectUi] = useTonConnectUI();
  const owner = useTonAddress(false);

  return useMutation<string, Error, DeployParams>(async (params) => {
    const tonClient = await getTonClient();
    const contract = tonClient.open(
      Deployer.createFromAddress(Address.parse(DeployerAddress))
    );
    const data = await contract.getContractData();

    const walletSeqno = await getSeqno(owner);

    const content = buildOnchainMetadata({
      name: params.name,
      image: params.image,
      description: params.description,
      symbol: params.symbol,
      decimals: params.decimals,
    });

    const [quorumNominator, quorumDenominator] = params.quorum.split("/");

    await tonConnectUi.sendTransaction({
      validUntil: Date.now() + 1000000,
      messages: [
        {
          address: Address.parse(DeployerAddress).toRawString(),
          amount: data.price.toString(),
          payload: Deployer.mintDaoMessage({
            ownerAddress: Address.parse(owner!),
            quorumNominator: parseInt(quorumNominator),
            quorumDenominator: parseInt(quorumDenominator),
            forwardTonAmount: toNano("0.002"),
            totalTonAmount: toNano("0.1"),
            content,
            jettonAmount: BigInt(
              formatDecimals(
                parseFloat(params.amount),
                parseInt(params.decimals)
              )
            ),
          })
            .toBoc()
            .toString("base64"),
        },
      ],
    });

    await waitWalletUpdate(owner, walletSeqno);

    const deployer = tonClient.open(
      Deployer.createFromAddress(Address.parse(DeployerAddress))
    );

    const daoAddress = await deployer.getDaoAddress(
      1,
      parseInt(params.quorum),
      content
    );
    return daoAddress.toString();
  });
};
